import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// Configuration Firebase
// Pour utiliser les vraies clés, créez un fichier .env.local avec :
// REACT_APP_FIREBASE_API_KEY=votre_cle_api
// REACT_APP_FIREBASE_AUTH_DOMAIN=conakryenpoche.com
// REACT_APP_FIREBASE_PROJECT_ID=ckry-f7bd7
// REACT_APP_FIREBASE_STORAGE_BUCKET=ckry-f7bd7.appspot.com
// REACT_APP_FIREBASE_MESSAGING_SENDER_ID=votre_sender_id
// REACT_APP_FIREBASE_APP_ID=votre_app_id

// Configuration Firebase - SÉCURITÉ : Utiliser uniquement des variables d'environnement
// Créer un fichier .env.production avec les valeurs suivantes :
// REACT_APP_FIREBASE_API_KEY=votre_cle_api
// REACT_APP_FIREBASE_AUTH_DOMAIN=conakryenpoche.com
// REACT_APP_FIREBASE_PROJECT_ID=ckry-f7bd7
// REACT_APP_FIREBASE_STORAGE_BUCKET=ckry-f7bd7.firebasestorage.app
// REACT_APP_FIREBASE_MESSAGING_SENDER_ID=votre_sender_id
// REACT_APP_FIREBASE_APP_ID=votre_app_id

// Vérifier que toutes les variables d'environnement sont définies
const requiredEnvVars = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// En production, toutes les variables doivent être définies
if (process.env.NODE_ENV === 'production') {
  const missingVars = Object.entries(requiredEnvVars)
    .filter(([key, value]) => !value)
    .map(([key]) => key);
  
  if (missingVars.length > 0) {
    throw new Error(
      `❌ SÉCURITÉ : Variables d'environnement Firebase manquantes : ${missingVars.join(', ')}\n` +
      `Veuillez créer un fichier .env.production avec toutes les variables requises.`
    );
  }
}

// Forcer l'utilisation du bucket firebasestorage.app qui est configuré avec CORS
// Le bucket appspot.com n'existe pas et cause des erreurs CORS
const getStorageBucket = () => {
  const bucket = requiredEnvVars.storageBucket;
  if (bucket && bucket.includes('appspot.com')) {
    // Remplacer appspot.com par firebasestorage.app
    return bucket.replace('.appspot.com', '.firebasestorage.app');
  }
  // Si le bucket n'est pas appspot.com, utiliser tel quel
  // Mais forcer firebasestorage.app si c'est le projet ckry-f7bd7
  if (requiredEnvVars.projectId === 'ckry-f7bd7') {
    return 'ckry-f7bd7.firebasestorage.app';
  }
  return bucket;
};

const effectiveStorageBucket = getStorageBucket();

// Journaliser le bucket effectif pour détecter rapidement une mauvaise config
if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'test') {
  console.info('[Firebase] Storage bucket configuré :', effectiveStorageBucket);
  if (
    process.env.NODE_ENV === 'production' &&
    requiredEnvVars.projectId === 'ckry-f7bd7' &&
    effectiveStorageBucket !== 'ckry-f7bd7.firebasestorage.app'
  ) {
    console.warn(
      '[Firebase][CORS] Attention : le bucket de stockage n\'est pas ckry-f7bd7.firebasestorage.app. ' +
      'Vérifiez REACT_APP_FIREBASE_STORAGE_BUCKET.'
    );
  }
}

const firebaseConfig = {
  apiKey: requiredEnvVars.apiKey,
  // Jamais exposer *.firebaseapp.com au public (e-mails Auth, redirects)
  authDomain: (() => {
    const raw = String(requiredEnvVars.authDomain || '').trim();
    if (!raw || raw.endsWith('.firebaseapp.com') || raw.endsWith('.web.app')) {
      return 'conakryenpoche.com';
    }
    return raw;
  })(),
  projectId: requiredEnvVars.projectId,
  storageBucket: effectiveStorageBucket,
  messagingSenderId: requiredEnvVars.messagingSenderId,
  appId: requiredEnvVars.appId
};

// Initialiser Firebase
const app = initializeApp(firebaseConfig);

// App Check (reCAPTCHA v3) — tokens envoyés automatiquement aux callables
const appCheckSiteKey =
  process.env.REACT_APP_APPCHECK_SITE_KEY || process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
/** @type {import('firebase/app-check').AppCheck | null} */
let appCheck = null;
if (appCheckSiteKey) {
  try {
    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      // Debug tokens: Firebase Console → App Check → Manage debug tokens
      // eslint-disable-next-line no-underscore-dangle
      window.FIREBASE_APPCHECK_DEBUG_TOKEN =
        window.FIREBASE_APPCHECK_DEBUG_TOKEN || true;
    }
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true
    });
  } catch (err) {
    console.warn('[Firebase] App Check init failed:', err?.message || err);
  }
}

// Initialiser les services
export const db = getFirestore(app);
export const auth = getAuth(app);
export const storage = getStorage(app);
// Spécifier la région us-central1 pour les fonctions (région par défaut de Firebase)
export const functions = getFunctions(app, 'us-central1');
export { appCheck };

export default app;




